import type { MasterData, TranslationResources } from '@models/master-data/master-data.model';
import type { Language } from '@models/i18n/language.model';
import { masterDataClient } from '@api/api-clients';
import { API_ENDPOINTS } from '@api/api.config';
import { i18n } from '@i18n/i18n.service';
import { useMasterDataStore } from '@store/master-data.store';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS, getMasterDataKey } from '@utils/session-keys.constants';
import { logger } from '@utils/logger.util';

/** In-memory per-language cache. Survives re-renders; lost on full page reload (session fills the gap). */
const memoryCache = new Map<Language, MasterData>();

/** Deduplicates simultaneous requests for the same language. */
const inFlight = new Map<Language, Promise<MasterData>>();
import {
  type AirportDto,
  type CountryDto,
  type CarrierDto,
  type GroupedStatesDto,
  type ResourceListDto,
  type QuestionnaireDto,
  type CodeTypeListDto,
  type MealsListDto,
  type EquipmentTypesListDto,
  type FareBrandsDto,
  type ExceptionListDto,
  type RouteMessagesDto,
  type AnnouncementsDto,
  type OlciBannersDto,
  type OfferCarouselsDto,
  type LifeEshopDto,
  type UpgradeOffersDto,
  type DetailUpgradesDto,
  type RandomSeatSelectionRulesDto,
  type AppSettingsDto,
  type TerminalsDto,
  mapMasterDataDto,
} from './master-data.mapper';

type MasterDataResponses = [
  { data: AirportDto },
  { data: CountryDto },
  { data: CarrierDto },
  { data: GroupedStatesDto },
  { data: ResourceListDto },
  { data: ResourceListDto },
  { data: QuestionnaireDto },
  { data: CodeTypeListDto },
  { data: MealsListDto },
  { data: EquipmentTypesListDto },
  { data: FareBrandsDto },
  { data: ExceptionListDto },
  { data: RouteMessagesDto },
  { data: AnnouncementsDto },
  { data: OfferCarouselsDto },
  { data: LifeEshopDto },
  { data: UpgradeOffersDto },
  { data: DetailUpgradesDto },
  { data: RandomSeatSelectionRulesDto },
  { data: AppSettingsDto },
  { data: TerminalsDto },
  { data: OlciBannersDto },
];

const isCacheValid = (cached: MasterData, language: Language): boolean => {
  return cached.language === language;
};

/* -------------------- NEW HELPERS (same file) -------------------- */

interface EndpointRequest {
  name: string;
  fetch: Promise<unknown>;
  isArray: boolean;
}

interface EndpointDescriptor {
  name: string;
  createFetch: (language: Language) => Promise<unknown>;
  isArray: boolean;
}

const endpointDescriptors: EndpointDescriptor[] = [
  {
    name: 'MASTER_DATA',
    createFetch: (language) =>
      masterDataClient.get<AirportDto>(API_ENDPOINTS.MASTER_DATA(language)),
    isArray: false,
  },
  {
    name: 'COUNTRY_LIST',
    createFetch: (language) =>
      masterDataClient.get<CountryDto>(API_ENDPOINTS.COUNTRY_LIST(language)),
    isArray: false,
  },
  {
    name: 'CARRIERS',
    createFetch: (language) => masterDataClient.get<CarrierDto>(API_ENDPOINTS.CARRIERS(language)),
    isArray: false,
  },
  {
    name: 'GROUPED_STATES',
    createFetch: (language) =>
      masterDataClient.get<GroupedStatesDto>(API_ENDPOINTS.GROUPED_STATES(language)),
    isArray: true,
  },
  {
    name: 'RESOURCE_LIST',
    createFetch: (language) =>
      masterDataClient.get<ResourceListDto>(API_ENDPOINTS.RESOURCE_LIST(language)),
    isArray: false,
  },
  {
    name: 'DOCUMENT_LIST',
    // Timatic document types: always load English list for all UI languages (temporary).
    createFetch: () => masterDataClient.get<ResourceListDto>(API_ENDPOINTS.DOCUMENT_LIST('en')),
    isArray: false,
  },
  {
    name: 'QUESTIONNAIRE',
    createFetch: (language) =>
      masterDataClient.get<QuestionnaireDto>(API_ENDPOINTS.QUESTIONNAIRE(language)),
    isArray: false,
  },
  {
    name: 'CODE_TYPE_LIST',
    createFetch: (language) =>
      masterDataClient.get<CodeTypeListDto>(API_ENDPOINTS.CODE_TYPE_LIST(language)),
    isArray: false,
  },
  {
    name: 'MEALS_LIST',
    createFetch: (language) =>
      masterDataClient.get<MealsListDto>(API_ENDPOINTS.MEALS_LIST(language)),
    isArray: false,
  },
  {
    name: 'EQUIPMENT_TYPES',
    createFetch: (language) =>
      masterDataClient.get<EquipmentTypesListDto>(API_ENDPOINTS.EQUIPMENT_TYPES(language)),
    isArray: false,
  },
  {
    name: 'FARE_BRANDS',
    createFetch: (language) =>
      masterDataClient.get<FareBrandsDto>(API_ENDPOINTS.FARE_BRANDS(language)),
    isArray: false,
  },
  {
    name: 'EXCEPTION_MESSAGES',
    createFetch: (language) =>
      masterDataClient.get<ExceptionListDto>(API_ENDPOINTS.EXCEPTION_MESSAGES(language)),
    isArray: false,
  },
  {
    name: 'ROUTE_MESSAGES',
    createFetch: (language) =>
      masterDataClient.get<RouteMessagesDto>(API_ENDPOINTS.ROUTE_MESSAGES(language)),
    isArray: false,
  },
  {
    name: 'ANNOUNCEMENTS',
    createFetch: (language) =>
      masterDataClient.get<AnnouncementsDto>(API_ENDPOINTS.ANNOUNCEMENTS(language)),
    isArray: false,
  },
  {
    name: 'OFFERS',
    createFetch: (language) =>
      masterDataClient.get<OfferCarouselsDto>(API_ENDPOINTS.OFFERS(language)),
    isArray: true,
  },
  {
    name: 'LIFE_BANNERS',
    createFetch: () => masterDataClient.get<LifeEshopDto>(API_ENDPOINTS.LIFE_BANNERS()),
    isArray: true,
  },
  {
    name: 'UPGRADE_OFFER',
    createFetch: (language) =>
      masterDataClient.get<UpgradeOffersDto>(API_ENDPOINTS.UPGRADE_OFFER(language)),
    isArray: true,
  },
  {
    name: 'DETAIL_UPGRADE',
    createFetch: (language) =>
      masterDataClient.get<DetailUpgradesDto>(API_ENDPOINTS.DETAIL_UPGRADE(language)),
    isArray: true,
  },
  {
    name: 'RANDOM_SEAT_SELECTION_RULES',
    createFetch: (language) =>
      masterDataClient.get<RandomSeatSelectionRulesDto>(
        API_ENDPOINTS.RANDOM_SEAT_SELECTION_RULES(language),
      ),
    isArray: false,
  },
  {
    name: 'APP_SETTINGS',
    createFetch: (language) =>
      masterDataClient.get<AppSettingsDto>(API_ENDPOINTS.APP_SETTINGS(language)),
    isArray: false,
  },
  {
    name: 'TERMINALS',
    createFetch: (language) =>
      masterDataClient.get<TerminalsDto>(API_ENDPOINTS.TERMINALS(language)),
    isArray: true,
  },
  {
    name: 'OLCI_BANNERS',
    createFetch: (language) =>
      masterDataClient.get<OlciBannersDto>(API_ENDPOINTS.OLCI_BANNERS(language)),
    isArray: true,
  },
];

const getEndpoints = (language: Language): EndpointRequest[] =>
  endpointDescriptors.map((descriptor) => ({
    name: descriptor.name,
    fetch: descriptor.createFetch(language),
    isArray: descriptor.isArray,
  }));

const mapSettledResponses = (
  settled: PromiseSettledResult<unknown>[],
  endpoints: ReturnType<typeof getEndpoints>,
): MasterDataResponses =>
  settled.map((res, index) => {
    if (res.status === 'fulfilled') {
      // Ensure the value is an object with a data property
      if (typeof res.value === 'object' && res.value !== null && 'data' in res.value) {
        return res.value;
      }
      // Fallback for unexpected fulfilled value
      return { data: endpoints[index].isArray ? ([] as unknown[]) : {} };
    }
    logger.warn(`Failed to load ${endpoints[index].name}`, res.reason);
    return { data: endpoints[index].isArray ? ([] as unknown[]) : {} };
  }) as MasterDataResponses;

/* -------------------- UPDATED FUNCTION (fixed ESLint) -------------------- */

const fetchMasterDataResponses = async (language: Language): Promise<MasterDataResponses> => {
  const endpoints = getEndpoints(language);

  const settled = await Promise.allSettled(endpoints.map((e) => e.fetch));

  return mapSettledResponses(settled, endpoints);
};

const mapResponses = (responses: MasterDataResponses, language: Language): MasterData => {
  const [
    airportsRes,
    countriesRes,
    carriersRes,
    groupedStatesRes,
    resourceListRes,
    documentListRes,
    questionnaireRes,
    codeTypeListRes,
    mealsListRes,
    equipmentTypesRes,
    fareBrandsRes,
    exceptionListRes,
    routeMessagesRes,
    announcementsRes,
    offersRes,
    lifeBannersRes,
    upgradeOffersRes,
    detailUpgradesRes,
    randomSeatSelectionRulesRes,
    appSettingsRes,
    terminalRes,
    olciBannersRes,
  ] = responses;

  return mapMasterDataDto(
    {
      airports: airportsRes.data,
      countries: countriesRes.data,
      carriers: carriersRes.data,
      groupedStates: groupedStatesRes.data,
      resourceLists: resourceListRes.data,
      documentList: documentListRes.data,
      questionnaire: questionnaireRes.data,
      codeTypeList: codeTypeListRes.data,
      mealsList: mealsListRes.data,
      equipmentTypesList: equipmentTypesRes.data,
      fareBrands: fareBrandsRes.data,
      exceptionList: exceptionListRes.data,
      routeMessages: routeMessagesRes.data,
      announcements: announcementsRes.data,
      olciBanners: olciBannersRes?.data,
      offers: offersRes.data,
      lifeBanners: lifeBannersRes.data,
      upgradeOffers: upgradeOffersRes.data,
      detailUpgrades: detailUpgradesRes.data,
      randomSeatSelectionRules:
        randomSeatSelectionRulesRes.data as unknown as RandomSeatSelectionRulesDto,
      appSettings: appSettingsRes.data,
      terminals: terminalRes.data,
    },
    language,
  );
};

const fetchFromApi = async (language: Language): Promise<MasterData> => {
  const responses = await fetchMasterDataResponses(language);
  return mapResponses(responses, language);
};

const isTranslationResources = (input: unknown): input is TranslationResources => {
  return typeof input === 'object' && input !== null;
};

const getTranslationsForLanguage = (language: Language): TranslationResources => {
  const resources = i18n.getResourceBundle(language, 'resources') as unknown;
  if (isTranslationResources(resources)) {
    return resources;
  }
  return {};
};

const includeTranslationsInMasterData = (
  masterData: MasterData,
  language: Language,
): MasterData => ({
  ...masterData,
  translations: getTranslationsForLanguage(language),
});

const setCachedMasterData = (language: Language, data: MasterData): void => {
  memoryCache.set(language, data);
  sessionStorageUtil.set(getMasterDataKey(language), data);
};

const getCachedMasterData = (language: Language): MasterData | null => {
  // 1. In-memory (fastest, within-session)
  const mem = memoryCache.get(language);
  if (mem) return mem;

  // 2. Per-language session storage (survives hard refresh)
  const stored = sessionStorageUtil.get<MasterData>(getMasterDataKey(language));
  if (stored && isCacheValid(stored, language)) {
    memoryCache.set(language, stored); // warm in-memory
    return stored;
  }

  // 3. Legacy single-key session storage (backwards compat with pre-cache data)
  const legacy = sessionStorageUtil.get<MasterData>(SESSION_KEYS.MASTER_DATA);
  if (legacy && isCacheValid(legacy, language)) {
    memoryCache.set(language, legacy);
    return legacy;
  }

  return null;
};

export const loadMasterData = async (language: Language): Promise<MasterData> => {
  const store = useMasterDataStore.getState();

  // Return immediately if already in cache
  const cached = getCachedMasterData(language);
  if (cached) {
    const data = includeTranslationsInMasterData(cached, language);
    logger.info(`Master data served from cache: ${language}`);
    store.setMasterData(data);
    return data;
  }

  // Deduplicate concurrent requests for the same language
  const existing = inFlight.get(language);
  if (existing) {
    logger.info(`Master data in-flight request reused: ${language}`);
    return existing;
  }

  store.setLoading(true);

  const request = fetchFromApi(language)
    .then((raw) => {
      const data = includeTranslationsInMasterData(raw, language);
      setCachedMasterData(language, data);
      store.setMasterData(data);
      logger.info(`Master data fetched from API: ${language}`);
      return data;
    })
    .catch((error: unknown) => {
      store.setError('Failed to load master data');
      logger.error('Master data fetch failed', error);
      throw error;
    })
    .finally(() => {
      inFlight.delete(language);
      store.setLoading(false);
    });

  inFlight.set(language, request);
  return request;
};

/**
 * Used during language switching.
 * Does NOT clear the cache — if data for `language` was loaded before it is reused immediately.
 * Only fetches from the API when language has never been loaded in this session.
 */
export const reloadMasterData = async (language: Language): Promise<MasterData> => {
  logger.info(`reloadMasterData called for: ${language}`);
  return loadMasterData(language);
};
